<template>
  <div class="TaskPanel">
    <b-overlay :show="saving" rounded="sm">
      <section class="task-panel-section">
        <div class="details-header">
          <h4 class="details-title">Chat Settings</h4>
          <div class="details-header-actions">
            <button type="button" class="btn btn-success btn-sm mr-2" @click="save" :disabled="saving" data-test="chat-settings-save">
              <b-spinner v-if="saving" small class="mr-2"></b-spinner>
              <i v-else class="fa fa-save mr-2"></i>
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closePanel" :disabled="saving">Cancel</button>
          </div>
        </div>

        <div class="card panel task-details-main">
          <div class="task-details-main-item">
            <label class="detail-label-block">Notifications</label>
            <b-form-checkbox v-model="soundPushEnabled" switch :disabled="saving" data-test="chat-sound-push-toggle">
              <span class="switch-label">Play sound &amp; desktop notifications for new chat messages</span>
            </b-form-checkbox>
          </div>

          <div v-if="soundPushEnabled" class="task-details-main-item">
            <NotificationSoundPicker
              v-model="notificationSound"
              label="Chat notification sound"
              hint="Click ▶ to preview. This sound plays when a teammate sends you a chat message."
            />
          </div>

          <div class="task-details-main-item">
            <NotificationHelp :testSoundFile="notificationSound" />
          </div>
        </div>
      </section>
    </b-overlay>
  </div>
</template>

<script>
import NotificationSoundPicker from '@/components/NotificationSoundPicker.vue';
import NotificationHelp from '@/components/NotificationHelp.vue';
import { getPrefs, setPref } from '@/utils/notificationPrefs';

export default {
  name: 'ChatSettings',
  components: { NotificationSoundPicker, NotificationHelp },

  data() {
    return {
      soundPushEnabled: true,
      notificationSound: 'elevator-ding.mp3',
      saving: false,
    };
  },

  computed: {
    userID() {
      return this.$store.state.customer.userID;
    },
  },

  created() {
    this.loadFromPrefs();
  },

  methods: {
    loadFromPrefs() {
      const prefs = getPrefs(this.userID);
      this.soundPushEnabled = prefs.chat.soundPush;
      this.notificationSound = prefs.chat.sound;
    },

    async save() {
      this.saving = true;
      try {
        setPref(this.userID, 'chat', {
          soundPush: this.soundPushEnabled,
          sound: this.notificationSound,
        });
        this.$toast.success('Chat settings saved!');
        this.$emit('closePanel', {});
      } catch (e) {
        console.error('ChatSettings save failed:', e);
        this.$toast.error('Failed to save chat settings. Please try again.');
      } finally {
        this.saving = false;
      }
    },

    closePanel() {
      this.$emit('closePanel', {});
    },
  },
};
</script>

<style lang="scss" scoped>
.TaskPanel {
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  color: #334155;
  background-color: #f8fafc;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.task-panel-section {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  flex: 1;
}

.details-header {
  margin-bottom: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.details-header-actions {
  display: flex;
  align-items: center;
}

.details-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.task-details-main {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 1rem;
  padding: 1rem;
}

.task-details-main-item {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-label-block {
  display: block;
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.switch-label {
  color: #0f172a;
  font-size: 0.9375rem;
}
</style>
